/**
 * 振替休暇申請書 の締め月まとめ（印刷時だけの集約）。
 *
 * レコードは1人1組（休日→出勤 / 出勤→休日）のまま。ここでやるのは「印刷する紙を
 * 束ねる」計算だけで、申請データ・承認ワークフローには一切触らない。
 *
 * 束ねる単位は「同じ締め月 × 同じ申請部署」。部署が混ざる月は部署ごとに別の紙になる。
 * 2026-09-16 変更: 以前は締め月をさらに7日ブロックに割って週ごとに別の紙にしていたが、
 * 申請履歴の期間フィルタ（例 2026年9月 = 8/16〜9/15）と紙の単位が合わず、同じ月の
 * 振替が複数枚に散った。週での分割は廃止し、選択した締め月まるごとで1束にする。
 *
 * 締め月の切り方: 給与締め（前月16日 → 当月15日）。振り分けに使う日付は 申請履歴 の
 * 一覧・期間フィルタと同じ getFormStartDate（= holiday_date || work_date）で、
 * created_at は使わない。1束が7行を超えたら 7行ずつの紙に分割する（用紙の行数制限）。
 */
import { cycleOf, getFormStartDate } from '@/pages/history/historyUtils';

export const ROWS_PER_SHEET = 7;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// 締め "YYYY-MM" の開始日（前月16日）と終了日（当月15日）。
export function cycleBounds(cycleKey) {
  const [y, m] = cycleKey.split('-').map(Number);
  return { start: new Date(y, m - 2, 16), end: new Date(y, m - 1, 15) };
}

/** 申請部署（form_data 優先・上位フィールドは写し）。束ねる単位に使う。 */
export function departmentOf(sub) {
  return ((sub.form_data || {}).department || sub.department || '').trim() || '（部署未設定）';
}


/** 締め月キー "2026-09" → "2026年9月"。用紙に出す見出し用。 */
export function cycleLabel(cycleKey) {
  const [y, m] = cycleKey.split('-').map(Number);
  return `${y}年${m}月`;
}

/** そのレコードが属する締め月＋申請部署。日付が無いレコードは null（束ねられない）。 */
export function cycleBucketOf(sub) {
  const refStr = getFormStartDate(sub);
  const ref = refStr ? new Date(refStr) : null;
  if (!ref || Number.isNaN(ref.getTime())) return null;
  const cycle = cycleOf(startOfDay(ref));
  const { start, end } = cycleBounds(cycle);
  const department = departmentOf(sub);
  return {
    // 締め月だけでなく申請部署まで含めて1枚にする（部署をまたいで1枚にはしない）
    key: `${cycle}#${department}`,
    cycle,
    label: cycleLabel(cycle),
    department,
    start: iso(start),
    end: iso(end),
  };
}

const sortKey = (s) => `${getFormStartDate(s) || ''}|${s.employee_name || ''}|${s.id}`;

/**
 * 印刷する紙の並びを決める。
 *   - leave_transfer: 締め月ごとにまとめ、7行ずつの紙に分割
 *   - それ以外の4種類: 今までどおり 1レコード = 1枚（この関数は素通し）
 * 並び順は元の選択順（最初に現れた位置に、その締め月の束を差し込む）。
 */
export function buildPrintPlan(records) {
  const groups = new Map();   // bucketKey（締め月＋申請部署）→ records
  const plan = [];
  records.forEach((r) => {
    if (r.form_type !== 'leave_transfer') {
      plan.push({ kind: 'single', submission: r });
      return;
    }
    const bucket = cycleBucketOf(r);
    const key = bucket ? bucket.key : `nodate#${r.id}`;
    if (!groups.has(key)) {
      groups.set(key, { bucket, records: [] });
      plan.push({ kind: 'leave_transfer', key });
    }
    groups.get(key).records.push(r);
  });

  const out = [];
  plan.forEach((entry) => {
    if (entry.kind === 'single') { out.push(entry); return; }
    const { bucket, records: rows } = groups.get(entry.key);
    const sorted = [...rows].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    const pageCount = Math.max(1, Math.ceil(sorted.length / ROWS_PER_SHEET));
    for (let p = 0; p < pageCount; p += 1) {
      out.push({
        kind: 'leave_transfer',
        key: `${entry.key}#p${p + 1}`,
        bucket,
        records: sorted.slice(p * ROWS_PER_SHEET, (p + 1) * ROWS_PER_SHEET),
        pageIndex: p + 1,
        pageCount,
      });
    }
  });
  return out;
}
